import { useMemo, useState } from "react";
import { Button, Checkbox, Dropdown } from "antd";
import { DownOutlined, HolderOutlined, UndoOutlined } from "@ant-design/icons";
import "./ColumnSettingsDropdown.scss";

const ColumnsIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="currentColor"
    className="columns-btn-icon"
    aria-hidden="true"
  >
    <path d="M1.5 2.5A1.5 1.5 0 0 1 3 1h10a1.5 1.5 0 0 1 1.5 1.5v11a1.5 1.5 0 0 1-1.5 1.5H3A1.5 1.5 0 0 1 1.5 13.5v-11zM6 2H3a.5.5 0 0 0-.5.5v11c0 .28.22.5.5.5h3V2zm1 0v12h3V2H7zm4 0v12h2a.5.5 0 0 0 .5-.5v-11a.5.5 0 0 0-.5-.5h-2z" />
  </svg>
);

const ColumnSettingsDropdown = ({
  availableOptions = [],
  visibleOrderedKeys = [],
  onToggleColumn,
  onToggleAll,
  onReorder,
  onReset,
  loading = false,
  isCustomized = false,
}) => {
  const [open, setOpen] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  const labelByKey = useMemo(() => {
    const map = new Map();
    availableOptions.forEach((option) => map.set(option.key, option.label));
    return map;
  }, [availableOptions]);

  const selectedSet = useMemo(
    () => new Set(visibleOrderedKeys),
    [visibleOrderedKeys],
  );

  const isAllSelected = useMemo(
    () =>
      availableOptions.length > 0 &&
      availableOptions.every((option) => selectedSet.has(option.key)),
    [availableOptions, selectedSet],
  );

  const isIndeterminate = useMemo(() => {
    const selectedCount = availableOptions.filter((option) =>
      selectedSet.has(option.key),
    ).length;
    return selectedCount > 0 && selectedCount < availableOptions.length;
  }, [availableOptions, selectedSet]);

  const handleToggleAll = (checked) => {
    if (onToggleAll) {
      onToggleAll(checked);
      return;
    }
    if (onToggleColumn) {
      if (checked) {
        availableOptions.forEach((option) => {
          if (!selectedSet.has(option.key)) {
            onToggleColumn(option.key);
          }
        });
      } else {
        availableOptions.forEach((option) => {
          if (selectedSet.has(option.key)) {
            onToggleColumn(option.key);
          }
        });
      }
    }
  };

  const panel = (
    <div className="column-settings-panel" onClick={(e) => e.stopPropagation()}>
      <div className="column-settings-header">
        <div className="column-settings-title-row">
          <span className="column-settings-title">Customize Columns</span>
          <span className="column-settings-count-pill">
            {visibleOrderedKeys.length} of {availableOptions.length} visible
          </span>
        </div>
        <div className="column-settings-hint">
          Toggle visibility or drag handles to reorder
        </div>
      </div>

      <div className="column-settings-select-all">
        <Checkbox
          indeterminate={isIndeterminate}
          checked={isAllSelected}
          disabled={loading || !availableOptions.length}
          onChange={(e) => handleToggleAll(e.target.checked)}
        >
          Select All
        </Checkbox>
      </div>

      <div className="column-settings-list">
        {availableOptions.map((option) => {
          const checked = selectedSet.has(option.key);
          const orderIndex = visibleOrderedKeys.indexOf(option.key);
          const canDrag = checked && orderIndex >= 0;

          return (
            <div
              key={option.key}
              className={`column-settings-item${checked ? " is-selected" : ""}${
                dragIndex === orderIndex ? " is-dragging" : ""
              }`}
              draggable={canDrag}
              onDragStart={(event) => {
                if (!canDrag) return;
                setDragIndex(orderIndex);
                event.dataTransfer.effectAllowed = "move";
                event.dataTransfer.setData("text/plain", String(orderIndex));
              }}
              onDragOver={(event) => {
                if (dragIndex === null) return;
                event.preventDefault();
                event.dataTransfer.dropEffect = "move";
              }}
              onDrop={(event) => {
                event.preventDefault();
                if (dragIndex === null || orderIndex < 0) return;
                onReorder?.(dragIndex, orderIndex);
                setDragIndex(null);
              }}
              onDragEnd={() => setDragIndex(null)}
            >
              <span className={`column-settings-handle${canDrag ? "" : " disabled"}`}>
                <HolderOutlined />
              </span>
              <Checkbox
                checked={checked}
                disabled={checked && visibleOrderedKeys.length <= 1}
                onChange={() => onToggleColumn?.(option.key)}
              >
                {labelByKey.get(option.key) || option.key}
              </Checkbox>
            </div>
          );
        })}
      </div>

      <div className="column-settings-footer">
        <Button
          type="link"
          size="small"
          icon={<UndoOutlined />}
          disabled={!isCustomized || loading}
          onClick={() => onReset?.()}
          className="column-settings-reset-btn"
        >
          Reset to default
        </Button>
      </div>
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={["click"]}
      placement="bottomRight"
      popupRender={() => panel}
      disabled={loading || !availableOptions.length}
    >
      <Button
        type="default"
        className={`columns-plus-btn columns-toolbar-btn${open ? " is-open" : ""}${
          isCustomized ? " is-customized" : ""
        }`}
        loading={loading}
        aria-label="Columns"
      >
        <span className="columns-btn-content">
          <ColumnsIcon />
          <span className="columns-btn-text">Columns</span>
          {isCustomized && (
            <span className="columns-btn-badge" title="Columns customized">
              {visibleOrderedKeys.length}
            </span>
          )}
          <DownOutlined className="columns-dropdown-arrow" />
        </span>
      </Button>
    </Dropdown>
  );
};

export default ColumnSettingsDropdown;
